// ── React Flow node type registry + shared presentational shell ─────────────
import type { ReactNode } from 'react'
import type { NodeTypes, Node } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import type { LucideIcon } from 'lucide-react'
import type { FlowNodeData } from '../../types'
import { StartNode } from './StartNode'
import { EndNode } from './EndNode'
import { WebhookNode } from './WebhookNode'
import { StepNode } from './StepNode'
import { BranchNode } from './BranchNode'

// Mirrors the FlowNode type used in graph.ts — FlowNodeData has no index
// signature, so it's intersected with Record<string, unknown> to satisfy
// React Flow's generic Node constraint.
export type FlowNode = Node<FlowNodeData & Record<string, unknown>>

export interface NodeShellProps {
  icon: LucideIcon
  label: string
  sublabel?: string
  /** Hex accent color driving the icon chip + selected border/glow. */
  accentColor: string
  selected?: boolean
  showTargetHandle?: boolean
  showSourceHandle?: boolean
  /** BRANCH nodes get a slightly more angular, diamond-flavored card. */
  diamond?: boolean
  children?: ReactNode
}

/** Shared presentational card every custom node renders through. */
export function NodeShell({
  icon: Icon,
  label,
  sublabel,
  accentColor,
  selected = false,
  showTargetHandle = true,
  showSourceHandle = true,
  diamond = false,
  children,
}: NodeShellProps) {
  return (
    <div
      className="relative flex items-center gap-2 rounded-lg border px-3 py-2.5 min-w-[170px] max-w-[220px] shadow-sm"
      style={{
        background: diamond ? '#141c30' : '#0f1729',
        borderColor: selected ? accentColor : '#1e2c44',
        boxShadow: selected ? `0 0 0 1px ${accentColor}` : undefined,
        clipPath: diamond ? 'polygon(4% 0, 100% 0, 96% 100%, 0 100%)' : undefined,
      }}
    >
      {showTargetHandle && (
        <Handle
          type="target"
          position={Position.Left}
          style={{ background: accentColor, width: 8, height: 8, border: 'none' }}
        />
      )}
      <div
        className="flex items-center justify-center rounded-md shrink-0"
        style={{ width: 26, height: 26, background: `${accentColor}22`, color: accentColor }}
      >
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-medium text-[var(--text-primary)] truncate">{label}</div>
        {sublabel && <div className="text-[10px] text-[#5b6b85] truncate">{sublabel}</div>}
        {children}
      </div>
      {showSourceHandle && (
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: accentColor, width: 8, height: 8, border: 'none' }}
        />
      )}
    </div>
  )
}

export const nodeTypes: NodeTypes = {
  start: StartNode,
  end: EndNode,
  webhook: WebhookNode,
  step: StepNode,
  branch: BranchNode,
}
